import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Mail, Lock, Phone } from "lucide-react";
import { PhoneAuthForm } from "@/components/PhoneAuthForm";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const redirectUri = window.location.origin + "/auth/callback";
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid email profile",
    prompt: "select_account",
    include_granted_scopes: "true",
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [authMode, setAuthMode] = useState("email"); // "email" | "phone"
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);

  useEffect(() => {
    api.get("/feature-flags")
      .then(({ data }) => setWhatsappEnabled(!!data?.enable_whatsapp_auth))
      .catch(() => {});
  }, []);

  const from = location.state?.from?.pathname || "/browse";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result?.needs_verification) {
        toast.message("Please verify your email to continue.");
        navigate(`/verify-otp?email=${encodeURIComponent(email)}`, { replace: true });
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setSubmitting(false); }
  };

  return (
    <main className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 py-10 fade-in">
      <div className="max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="login-logo">
          <img src="/logo.png" alt="Junior Shelf logo" className="h-16 w-auto object-contain" />
          <div className="font-display text-xl font-semibold text-slate-900">Junior Shelf</div>
        </Link>

        <div className="card-soft p-8">
          <h1 className="font-display text-2xl font-semibold text-slate-900 mb-1">Welcome back</h1>
          <p className="text-slate-500 text-sm mb-6">Sign in to borrow, lend, and track your shelf.</p>

          <button
            onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-3 py-3 border-2 border-slate-200 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            data-testid="login-google-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-semibold">OR</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {whatsappEnabled && (
          <div className="flex gap-2 mb-5 p-1 bg-slate-100 rounded-full" data-testid="login-mode-toggle">
            <button
              type="button"
              onClick={() => setAuthMode("email")}
              className={`flex-1 text-sm font-semibold py-2 rounded-full transition-colors ${authMode === "email" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
              data-testid="login-mode-email"
            >
              <Mail size={14} className="inline mr-1.5 -mt-0.5" /> Email
            </button>
            <button
              type="button"
              onClick={() => setAuthMode("phone")}
              className={`flex-1 text-sm font-semibold py-2 rounded-full transition-colors ${authMode === "phone" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
              data-testid="login-mode-phone"
            >
              <Phone size={14} className="inline mr-1.5 -mt-0.5" /> Phone
            </button>
          </div>
          )}

          {whatsappEnabled && authMode === "phone" ? (
            <PhoneAuthForm testidPrefix="login-phone" />
          ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-warm pl-10" data-testid="login-email" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Password</label>
                <Link to="/forgot-password" className="text-xs font-semibold text-orange-600 hover:text-orange-700" data-testid="login-forgot-link">
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-warm pl-10" data-testid="login-password" />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="login-submit">
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
          )}

          <div className="text-sm text-center text-slate-500 mt-6">
            New to Junior Shelf?{" "}
            <Link to="/register" className="text-orange-600 font-semibold hover:text-orange-700" data-testid="login-register-link">Create an account</Link>
          </div>
        </div>
      </div>
    </main>
  );
}
